import React, { useState, useEffect } from 'react';
import { AlertTriangle, Clock, MapPin, CheckCircle2, Plus, X, Send } from 'lucide-react';
import { BuildingBlock, Zone } from '../../types';
import { PhotoUploader } from './PhotoUploader';

export const DowntimeLog: React.FC = () => {
  const [downtimes, setDowntimes] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);

  // Form State
  const [blocks, setBlocks] = useState<BuildingBlock[]>([]);
  const [zones, setZones] = useState<Zone[]>([]);
  const [selectedBlockId, setSelectedBlockId] = useState<string>('block-a');
  const [selectedZoneId, setSelectedZoneId] = useState<string>('');
  const [reasonCategory, setReasonCategory] = useState<string>('walls_not_plastered');
  const [hoursLost, setHoursLost] = useState<number>(2);
  const [description, setDescription] = useState<string>('');
  const [photos, setPhotos] = useState<string[]>([]);
  const [submitting, setSubmitting] = useState(false);

  const fetchDowntimes = () => {
    setLoading(true);
    fetch('/api/v1/foreman/incidents')
      .then((r) => r.json())
      .then((d) => {
        if (d.success) setDowntimes(d.data);
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchDowntimes();

    fetch('/api/v1/projects/proj-sz-2026/tree')
      .then((r) => r.json())
      .then((d) => {
        if (d.success) {
          setBlocks(d.data.blocks);
          setZones(d.data.zones);
          if (d.data.zones.length > 0) setSelectedZoneId(d.data.zones[0].id);
        }
      });
  }, []);

  const getReasonLabel = (cat: string) => {
    switch (cat) {
      case 'walls_not_plastered':
      case 'no_wall_readiness':
        return 'Стены не оштукатурены / нет готовности';
      case 'grsh_flooded':
      case 'flooded_switchroom':
        return 'Помещение ГРЩ/ВРУ затоплено / заблокировано';
      case 'ventilation_collision':
      case 'vent_collision':
        return 'Коллизия с вентиляцией / ОВ / ВК';
      case 'no_temporary_power':
        return 'Нет временного питания на объекте';
      case 'material_delay':
      case 'cable_delivery_delay':
        return 'Задержка поставки материалов';
      default:
        return 'Прочее';
    }
  };

  const handleCreateIncident = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!description.trim()) {
      alert('Пожалуйста, введите описание причины простоя.');
      return;
    }

    setSubmitting(true);
    try {
      const res = await fetch('/api/v1/foreman/incidents', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          blockId: selectedBlockId,
          zoneId: selectedZoneId,
          reasonCategory,
          description,
          hoursLost,
          photoUrls: photos,
          status: 'open',
        }),
      });

      const data = await res.json();
      if (data.success) {
        setShowModal(false);
        setDescription('');
        setPhotos([]);
        fetchDowntimes();
      } else {
        alert(`Ошибка при сохранении: ${data.error}`);
      }
    } catch (err) {
      console.error(err);
      alert('Ошибка соединения с сервером.');
    } finally {
      setSubmitting(false);
    }
  };

  const totalLostHours = downtimes.reduce((acc, dt) => acc + (dt.hours_lost || 0), 0);
  const filteredZones = zones.filter((z) => z.blockId === selectedBlockId);
  const selectedZone = zones.find((z) => z.id === selectedZoneId);

  return (
    <div className="bg-white border border-slate-200/90 rounded-2xl p-4 sm:p-6 shadow-[0_4px_20px_-4px_rgba(0,0,0,0.05)] max-w-4xl mx-auto space-y-4">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-b border-slate-100 pb-4">
        <div>
          <h2 className="text-lg font-bold text-slate-900 flex items-center gap-2">
            <span className="p-2 bg-rose-50 text-rose-700 border border-rose-200 rounded-xl">
              <AlertTriangle className="w-5 h-5" />
            </span>
            <span>Журнал простоев и инцидентов СМР</span>
          </h2>
          <p className="text-xs text-slate-500 mt-1">
            Фиксация строительных причин простоя для защиты графика перед Генподрядчиком и Заказчиком
          </p>
        </div>

        <div className="flex items-center gap-3">
          <div className="bg-amber-50 border border-amber-200 px-3 py-1.5 rounded-xl text-right">
            <span className="text-[10px] text-amber-700 block font-bold uppercase">Суммарный простой</span>
            <span className="text-sm font-black text-amber-900">{totalLostHours} чел/часов</span>
          </div>

          <button
            type="button"
            onClick={() => setShowModal(true)}
            className="flex items-center gap-1.5 bg-rose-600 hover:bg-rose-700 text-white font-bold text-xs px-3 py-2.5 rounded-xl shadow-md shadow-rose-600/20 transition active:scale-95 whitespace-nowrap"
          >
            <Plus className="w-4 h-4" />
            <span>Фиксировать простой</span>
          </button>
        </div>
      </div>

      {/* Incident Modal Form */}
      {showModal && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white border border-slate-200 rounded-3xl p-6 max-w-lg w-full space-y-4 shadow-2xl animate-fade-in max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between border-b border-slate-100 pb-3">
              <h3 className="text-base font-bold text-slate-900 flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-rose-600" />
                <span>Фиксация простоя / инцидента</span>
              </h3>
              <button
                type="button"
                onClick={() => setShowModal(false)}
                className="text-slate-400 hover:text-slate-600 p-1.5 rounded-full hover:bg-slate-100 transition"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleCreateIncident} className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-slate-600 font-semibold block mb-1">Корпус / Блок</label>
                  <select
                    value={selectedBlockId}
                    onChange={(e) => setSelectedBlockId(e.target.value)}
                    className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-xs text-slate-800 focus:outline-none focus:border-rose-500"
                  >
                    {blocks.map((b) => (
                      <option key={b.id} value={b.id}>
                        {b.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="text-xs text-slate-600 font-semibold block mb-1">Зона / Захватка</label>
                  <select
                    value={selectedZoneId}
                    onChange={(e) => setSelectedZoneId(e.target.value)}
                    className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-xs text-slate-900 font-bold focus:outline-none focus:border-rose-500"
                  >
                    {filteredZones.map((z) => (
                      <option key={z.id} value={z.id}>
                        {z.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label className="text-xs text-slate-600 font-semibold block mb-1">Причина простоя (Классификатор)</label>
                <select
                  value={reasonCategory}
                  onChange={(e) => setReasonCategory(e.target.value)}
                  className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2.5 text-xs text-slate-900 font-bold focus:outline-none focus:border-rose-500"
                >
                  <option value="walls_not_plastered">Стены не оштукатурены / нет готовности</option>
                  <option value="grsh_flooded">Помещение ГРЩ/ВРУ затоплено / заблокировано</option>
                  <option value="ventilation_collision">Коллизия трасс с вентиляцией / ОВ / ВК</option>
                  <option value="no_temporary_power">Нет временного электроснабжения на объекте</option>
                  <option value="material_delay">Задержка поставки материалов</option>
                  <option value="other">Прочее</option>
                </select>
              </div>

              <div>
                <label className="text-xs text-slate-600 font-semibold block mb-1">Потеряно времени (чел/часов)</label>
                <input
                  type="number"
                  step="0.5"
                  min="0.5"
                  value={hoursLost}
                  onChange={(e) => setHoursLost(parseFloat(e.target.value) || 1)}
                  className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-sm text-rose-700 font-black focus:outline-none focus:border-rose-500"
                />
              </div>

              <div>
                <label className="text-xs text-slate-600 font-semibold block mb-1">Подробное описание инцидента</label>
                <textarea
                  rows={3}
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Опишите характер препятствия (например: Штукатуры не передали замок 10 этажа, помещение ГРЩ завалено поддонами)..."
                  className="w-full bg-slate-50 border border-slate-200 rounded-xl p-2.5 text-xs text-slate-800 focus:outline-none focus:border-rose-500"
                />
              </div>

              {/* Photo uploader with Canvas compression */}
              <PhotoUploader
                photos={photos}
                zoneName={selectedZone?.name}
                onChange={(newPhotos) => setPhotos(newPhotos)}
              />

              <div className="pt-2 flex gap-3">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="flex-1 bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold py-2.5 rounded-xl text-xs transition border border-slate-200"
                >
                  Отмена
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="flex-1 bg-rose-600 hover:bg-rose-700 text-white font-bold py-2.5 rounded-xl text-xs shadow-md shadow-rose-600/20 transition flex items-center justify-center gap-1.5 disabled:opacity-50"
                >
                  <Send className="w-4 h-4" />
                  <span>Сохранить простой</span>
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Downtimes List */}
      {loading ? (
        <div className="p-8 text-center text-slate-400 text-sm">Загрузка журнала простоев...</div>
      ) : downtimes.length === 0 ? (
        <div className="p-8 text-center bg-slate-50 rounded-2xl border border-slate-200 text-slate-500 text-sm flex flex-col items-center gap-2">
          <CheckCircle2 className="w-8 h-8 text-emerald-600" />
          <span>Простоев не зафиксировано. Все звенья работают по графику!</span>
        </div>
      ) : (
        <div className="space-y-3">
          {downtimes.map((dt) => (
            <div key={dt.id} className="bg-slate-50/80 border border-slate-200 hover:border-amber-300 p-4 rounded-xl space-y-2 transition shadow-sm">
              <div className="flex items-start justify-between gap-3">
                <div className="space-y-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-xs font-bold text-amber-800 bg-amber-50 px-2.5 py-0.5 rounded-lg border border-amber-200">
                      {getReasonLabel(dt.reason_category || dt.reason)}
                    </span>
                    <span className="text-xs text-slate-500 flex items-center gap-1">
                      <MapPin className="w-3.5 h-3.5 text-slate-400" />
                      {dt.block_name || 'Блок А'} • {dt.floor_name || 'Этаж'} • {dt.zone_name || 'Зона'}
                    </span>
                  </div>
                  <p className="text-sm text-slate-800 font-medium">{dt.description}</p>
                </div>

                <div className="text-right shrink-0">
                  <span className="text-xs font-bold text-rose-700 bg-rose-50 px-2.5 py-1 rounded-lg border border-rose-200 flex items-center gap-1">
                    <Clock className="w-3.5 h-3.5" />
                    -{dt.hours_lost || dt.hoursLost || 1} ч
                  </span>
                  <span className="text-[10px] text-slate-400 block mt-1">
                    {dt.created_at ? new Date(dt.created_at).toLocaleDateString('ru-RU') : new Date().toLocaleDateString('ru-RU')}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
